import {
  CustomerModelBulkItemModel,
  GeneralConfigProfileDto,
  GeneralConfigProfileModel,
  PrintingDefaultDto,
  PrintingDefaultModelBulkItemModel,
  RelationshipListItemDto
} from 'src/models/relationshipmanagement';
import { ConsignmentSettingsGroupModels, DefaultsState } from './types';
import { Dispatch } from 'react';
import {
  bulkDeletePrintingDefaults,
  bulkPutPrintingDefaults,
  getGeneralConfigProfiles
} from 'src/services/relationshipmanagement';
import { v4 as uuidv4 } from 'uuid';
import { AssignedCustomersState } from '../util/types';
import { ModeValue } from 'src/types/uiflow/uiflow-types';

// The Palletline printing type was originally seeded with the code 'Contrado'
const PALLETLINE_PRINTING_TYPE_CODES = ['contrado', 'palletline'];

export const isPalletlinePrintingType = (code?: string) =>
  PALLETLINE_PRINTING_TYPE_CODES.includes(code?.trim().toLowerCase() ?? '');

export function etaRangeRoundingTypeSource(t: (text?: string | undefined) => string) {
  return [
    {
      id: '0',
      value: t('Do Not Round')
    },
    {
      id: '1',
      value: t('To the nearest 5 minutes')
    },
    {
      id: '2',
      value: t('To the nearest 10 minutes')
    },
    {
      id: '3',
      value: t('To the nearest 15 minutes')
    },
    {
      id: '4',
      value: t('To the nearest 30 minutes')
    },
    {
      id: '5',
      value: t('To the nearest hour')
    }
  ];
}

export function etaDisplayRangeUnitSource(t: (text?: string | undefined) => string) {
  return [
    {
      id: '0',
      value: t('Hour')
    },
    {
      id: '1',
      value: t('Minute')
    }
  ];
}

export function etaDisplayTypeSource(t: (text?: string | undefined) => string) {
  return [
    {
      id: '0',
      value: t(`Don't Display ETA`)
    },
    {
      id: '1',
      value: t('Display ETA')
    },
    {
      id: '2',
      value: t('Display ETA Range')
    }
  ];
}

export function buildPrintingDefaultsState(
  res: PrintingDefaultDto[],
  setPrintingDefaultsState: Dispatch<DefaultsState[]>
) {
  if (res.length == 0) return;

  const tableArray: DefaultsState[] = [];
  for (let index = 0; index < res.length; index++) {
    tableArray.push({
      id: res[index].id ?? '',
      printingTypeId: res[index].printingTypeId ?? '',
      quantity: res[index].quantity ?? 0,
      printAsDefault: res[index].printAsDefault ?? false,
      labelId: res[index].labelId ?? '',
      version: res[index].version
    });
  }

  setPrintingDefaultsState(tableArray);
}

export function generateConsignmentSettingsGroupModels(
  id: string,
  printingDefaultsState: DefaultsState[],
  existingDefaults: PrintingDefaultDto[],
  existingCustomers: RelationshipListItemDto[],
  newState: AssignedCustomersState[]
): ConsignmentSettingsGroupModels {
  const updateModels: PrintingDefaultModelBulkItemModel[] = [];
  const deleteModels: PrintingDefaultModelBulkItemModel[] = [];
  const assignedCustomersModels: CustomerModelBulkItemModel[] = [];

  /**
   * Add new Printing Defaults
   */
  for (let printingDefaultsIndex = 0; printingDefaultsIndex < printingDefaultsState.length; printingDefaultsIndex++) {
    const printingDefault: DefaultsState = printingDefaultsState[printingDefaultsIndex];

    if (existingDefaults.length > 0 && existingDefaults.find(x => x.id === printingDefault.id)) {
      const element = existingDefaults.find(x => x.id === printingDefault.id);
      updateModels.push({
        operationId: uuidv4(),
        id: element?.id ?? '',
        model: {
          generalConfigProfileId: id,
          printAsDefault: printingDefault.printAsDefault,
          printingTypeId: printingDefault.printingTypeId,
          quantity: printingDefault.quantity,
          networkId: printingDefault.networkId,
          labelId: printingDefault.labelId ?? '',
          version: element?.version ?? ''
        }
      });

      continue;
    }

    updateModels.push({
      operationId: uuidv4(),
      id: '',
      model: {
        generalConfigProfileId: id,
        printAsDefault: printingDefault.printAsDefault,
        printingTypeId: printingDefault.printingTypeId,
        networkId: printingDefault.networkId,
        quantity: printingDefault.quantity,
        labelId: printingDefault.labelId ?? ''
      }
    });
  }

  /**
   * Delete Printing Defaults
   */
  for (let index = 0; index < existingDefaults.length; index++) {
    const existingDefault: PrintingDefaultDto = existingDefaults[index];

    if (printingDefaultsState.find(x => x.id === existingDefault.id) === undefined) {
      deleteModels.push({
        operationId: uuidv4(),
        id: existingDefault.id,
        model: {
          version: existingDefault.version,
          labelId: existingDefault.labelId ?? ''
        }
      });
    }
  }

  /**
   * Assign group to new customers
   */
  for (let newStateIndex = 0; newStateIndex < newState.length; newStateIndex++) {
    const assignedCustomer: AssignedCustomersState = newState[newStateIndex];

    if (existingCustomers.length > 0 && existingCustomers.find(x => x.id === assignedCustomer.id)) {
      // Profile is already assigned and hasn't changed so can ignore here
      continue;
    }

    assignedCustomersModels.push({
      operationId: uuidv4(),
      id: assignedCustomer.id,
      model: {
        generalConfigProfileId: id
      }
    });
  }

  /**
   * Remove group if customer was deleted from table
   */
  for (let index = 0; index < existingCustomers.length; index++) {
    if (newState.find(x => x.id === existingCustomers[index].id) === undefined) {
      assignedCustomersModels.push({
        operationId: uuidv4(),
        id: existingCustomers[index].id,
        model: {
          generalConfigProfileId: '00000000-0000-0000-0000-000000000000'
        }
      });
    }
  }

  return { updateModels: updateModels, deleteModels: deleteModels, assignedCustomersModels: assignedCustomersModels };
}

export function buildConsignmentSettingsGroupModel(data: any): GeneralConfigProfileModel {
  return {
    description: data.description ?? '',
    name: data.name ?? '',
    customerPaperworkAllowed: data.customerPaperworkAllowed ?? false,
    customerPaperworkAllowEditAfterCollectionDate: data.customerPaperworkAllowEditAfterCollectionDate ?? false,
    customerPaperworkAllowEditAfterCutOffTime: data.customerPaperworkAllowEditAfterCutOffTime ?? false,
    customerPaperworkMandatory: data.customerPaperworkMandatory ?? false,
    etaDisplayHighThreshold: data.etaDisplayHighThreshold ?? '',
    etaDisplayLowThreshold: data.etaDisplayLowThreshold ?? '',
    etaDisplayRangeUnit: data.etaDisplayRangeUnit ?? '',
    etaDisplayRangeValue: data.etaDisplayRangeValue ?? '',
    etaDisplaySetThreshold: data.etaDisplaySetThreshold ?? false,
    etaDisplayType: data.etaDisplayType ?? '',
    etaRangeRoundingType: data.etaRangeRoundingType ?? '',
    allowEditingColNote1: data.allowEditingColNote1 ?? false,
    allowEditingColNote2: data.allowEditingColNote2 ?? false,
    allowEditingColNote3: data.allowEditingColNote3 ?? false,
    allowEditingColNote4: data.allowEditingColNote4 ?? false,
    allowEditingDelNote1: data.allowEditingDelNote1 ?? false,
    allowEditingDelNote2: data.allowEditingDelNote2 ?? false,
    allowEditingDelNote3: data.allowEditingDelNote3 ?? false,
    allowEditingDelNote4: data.allowEditingDelNote4 ?? false,
    allowManualAddressEntry: data.allowManualAddressEntry ?? false,
    automaticSalesOrderNumbering: data.automaticSalesOrderNumbering ?? false,
    activateCollectionTime: data.activateCollectionTime ?? false,
    allowMultipleOstypes: data?.allowMultipleOstypes ?? true, // default true
    allowTotalSpacesEdit: data?.allowTotalSpacesEdit ?? false,
    restrictReferenceCharacters: data?.restrictReferenceCharacters ?? false,
    invalidReferenceCharacters: data?.invalidReferenceCharacters ?? '',
    isActive: data.isActive ?? false,
    isDefault: data.isDefault ?? false,
    selectLabelOnPrint: data.selectLabelOnPrint ?? false,
    version: data.version ?? '',
    dashboardId: data.dashboardId ?? '',
    enforceCollectionContactName: data.enforceCollectionContactName ?? false,
    enforceCollectionContactPhone: data.enforceCollectionContactPhone ?? false,
    enforceDeliveryContactName: data.enforceDeliveryContactName ?? false,
    enforceDeliveryContactPhone: data.enforceDeliveryContactPhone ?? false
  };
}

export async function handleUpdateDefaultModels(
  updateModels: PrintingDefaultModelBulkItemModel[],
  deleteModels: PrintingDefaultModelBulkItemModel[]
) {
  if (updateModels.length > 0) {
    await bulkPutPrintingDefaults(updateModels).then(async () => {
      if (deleteModels.length > 0) {
        await bulkDeletePrintingDefaults(deleteModels);
      }
    });
  } else if (deleteModels.length > 0) {
    await bulkDeletePrintingDefaults(deleteModels);
  }
}

export async function handleSameNameCheck(
  uiFlowMode: ModeValue,
  nameToCheck: string,
  nameOnServer?: string,
  id?: string
): Promise<boolean> {
  let groupsWithSameName: GeneralConfigProfileDto[] = [];

  if (uiFlowMode === 'add') {
    groupsWithSameName = (await getGeneralConfigProfiles({ filter: `name eq ${nameToCheck}` })).data;

    if (groupsWithSameName.length > 0) {
      // A group with the same name already exists

      return false;
    }
  } else if (uiFlowMode === 'edit') {
    // Has the name changed?
    if (nameToCheck !== nameOnServer) {
      groupsWithSameName = (await getGeneralConfigProfiles({ filter: `name eq ${nameToCheck}` })).data;

      if (groupsWithSameName.length > 0 && groupsWithSameName[0].id !== id) {
        // A group with the same name already exists

        return false;
      }
    }
  }

  return true;
}
